import React, { useState, useRef, useEffect } from 'react';
import { AudioLines, Loader2, ArrowUp } from 'lucide-react';
import { Theme } from '../types/theme';
import MessageList from './MessageList';
import { chatService } from '../api/chatService';
import { sessionManager } from '../utils/sessionManager';
import MicrophoneInput from './MicrophoneInput';
import { hexToRgba } from '../utils/helper';
import { ActiveView, ViewMode } from '../types';
import { BookingForm, ConfirmationCard, InitialChatScreen } from './chat_components';
import { useSessionState } from '../hooks/useSessionState';

interface ChatInterfaceProps {
  viewMode: ViewMode;
  onViewChange: (view: ActiveView) => void;
  theme: Theme;
  agentName?: string;
  messages: Message[];
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  userName: string | null;
  sessionId: string;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  onModeChange?: (mode: ViewMode) => void;
  onThemeChange?: (theme: Theme) => void;
  onClearSession?: () => void;
  showVoiceInSplit?: boolean;
  onToggleVoice?: () => void;
  preflightVoiceCheck?: () => Promise<boolean>;
  updateIsProfileUpdated?: (isProfileUpdated: boolean) => void;
  companyName?: string;
}
export interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  files?: File[];
  timestamp: Date;
  intent?: string;
  isLoading?: boolean;
  error?: string;
  component?: React.ReactNode;
  stream?: boolean;
  partial?: string;
}
export interface BookingDetails {
  name?: string;
  email?: string;
  phone?: string;
}
const ChatInterface: React.FC<ChatInterfaceProps> = ({
  viewMode,
  onModeChange,
  onViewChange,
  theme,
  onThemeChange,
  agentName,
  messages,
  addMessage,
  updateMessage,
  userName,
  sessionId,
  setMessages,
  showVoiceInSplit,
  onToggleVoice,
  preflightVoiceCheck,
  companyName,
}) => {

  const [inputValue, setInputValue] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCheckingVoice, setIsCheckingVoice] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [isOtp, setIsOtp] = useState(false);
  const [mode, setMode] = useState<string>('');
  const isProfileUpdated = localStorage.getItem("isProfileUpdated") === "true";
  const [bookingDetails, setBookingDetails] = useState<BookingDetails>({});
  const { updateIsProfileUpdated } = useSessionState()
  // Demo booking state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  // Initialize session on component mount
  useEffect(() => {
    const initializeSession = async () => {
      const urlSessionId = sessionManager.getSessionIdFromUrl();
      if (urlSessionId) {
        setCurrentSessionId(urlSessionId);
        chatService.saveSessionId(urlSessionId);
      }
    };

    initializeSession();

    // Handle browser navigation
    const cleanup = sessionManager.handleBrowserNavigation(async (sessionId) => {
      if (sessionId && sessionId !== currentSessionId) {
        setCurrentSessionId(sessionId);
        chatService.saveSessionId(sessionId);
      } else if (!sessionId) {
        setCurrentSessionId(null);
        chatService.clearSession();
      }
    });

    return cleanup;
  }, [currentSessionId]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '45px';
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 45), 150);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);



  const handleVoiceButtonClick = async () => {
    setIsCheckingVoice(true);
    if (!isProfileUpdated) {
      setIsLimitExceeded(true);
      setMode("voice")
      return;
    }
    try {
      // const hasAccess = await checkMediaPermissions();
      // if (!hasAccess) return;

      // NEW: run parent provided preflight check (shows toasts on error)
      if (preflightVoiceCheck) {
        const ok = await preflightVoiceCheck();
        if (!ok) return;
      }

      if (viewMode === 'fullscreen') {
        onToggleVoice?.();
      } else {
        onViewChange("voice");
      }
    } finally {
      setIsCheckingVoice(false);
    }
  };
  const typeText = async (
    fullText: string,
    onUpdate: (partial: string) => void,
    speed = 12
  ) => {
    let accumulated = "";

    for (let i = 0; i < fullText.length; i++) {
      accumulated += fullText[i];
      onUpdate(accumulated);
      await new Promise((res) => setTimeout(res, speed));
    }
  };


  const handleSendMessage = async (messageText?: string) => {
    // Determine text
    let textToSend: string;
    if (typeof messageText === "string") textToSend = messageText.trim();
    else textToSend = inputValue.trim();

    if (!textToSend && uploadedFiles.length === 0) return;

    // Add user message
    addMessage({
      type: "user",
      content: textToSend,
      files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
    });

    resetInputToInitial();
    setIsLoading(true);

    // Create an assistant placeholder
    const assistantId = addMessage({
      type: "assistant",
      content: "",
      isLoading: true,
    });

    try {
      const response = await chatService.sendMessage(textToSend, {
        userName: userName || undefined,
      });

      if (response.intent === 'request_contact_info') {
        setIsLimitExceeded(true);
        setMessages(perv => perv.slice(0, -2))
      }
      else {
        const fullReply = response.response;
        // Start streaming (typing)
        await typeText(fullReply, (partial) => {
          updateMessage(assistantId, {
            content: partial,
            isLoading: false, // typing starts, hide dots
          });
        });

        // Finished typing
        updateMessage(assistantId, {
          intent: response.intent,
          isLoading: false,
        });

        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }

    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to send message";

      updateMessage(assistantId, {
        content:
          "I apologize, but I encountered an error processing your message. Please try again.",
        isLoading: false,
        error: errorMessage,
      });
    } finally {
      setIsLoading(false);
      resetInputToInitial();
    }
  };


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only send if not loading and there is text or files
      if (!isLoading && (inputValue.trim() || uploadedFiles.length > 0)) {
        handleSendMessage();
      }
    }
  };

  const handleSpeechRecognized = (text: string) => {
    if (text.trim()) {
      setInputValue((prev) => (prev ? prev + " " + text.trim() : text.trim()));
    }
  };

  // Reset input UI to initial state (height, files, listening)
  const resetInputToInitial = () => {
    setInputValue('');
    setUploadedFiles([]);
    setIsListening(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = '45px';
      setTimeout(() => {
        textareaRef.current?.focus();
        const len = textareaRef.current?.value.length ?? 0;
        try {
          textareaRef.current?.setSelectionRange(len, len);
        } catch {
          console.warn('Failed to set cursor position in textarea');
        }
      }, 0);
    }
  };

  const handleFileSelect = (files: File[]) => {
    setUploadedFiles(prev => [...prev, ...files]);
  };

  // const handleRemoveFile = (index: number) => {
  //   setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  // };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  };
  const handleBookingSubmit = async (details: BookingDetails) => {
    setIsLoading(true);
    setBookingDetails(details);
    try {
      const response = await chatService.updateProfile(sessionId, details);
      setIsLimitExceeded(false);
      if (response.status === "success") {
        setIsOtp(true);
      }
    } catch (e) {
      console.log("Booking Form Submitted chat", e);
    }
    finally {
      setIsLoading(false);
    }

  }
  const handleOtpSubmit = async (otp: string) => {
    setIsLoading(true);
    try {
      const response = await chatService.verifyOtp(sessionId, otp);
      if (response.status === "success") {
        setIsOtp(false);
        updateIsProfileUpdated(true);
        if (mode === 'voice') {
          try {
            // const hasAccess = await checkMediaPermissions();
            // if (!hasAccess) return;

            // NEW: run parent provided preflight check (shows toasts on error)
            if (preflightVoiceCheck) {
              const ok = await preflightVoiceCheck();
              if (!ok) return;
            }

            if (viewMode === 'fullscreen') {
              onToggleVoice?.();
            } else {
              onViewChange("voice");
            }
          } finally {
            setIsCheckingVoice(false);
          }
        }
        else {
          handleSendMessage(response.pending_message);
        }
      }
    } catch (e) {
      console.log("Booking Form Submitted chat", e);
    }
    finally {
      setIsLoading(false);
    }

  }

  // Truncate long names for the placeholder (adds an ellipsis)
  const truncate = (s: string, max = 12) => (s && s.length > max ? s.slice(0, max - 1) + '…' : s);

  return (
    <div
      className={`flex flex-col transition-colors duration-300 h-full`}
      style={{
        backgroundColor: isDragOver ? `${theme.primaryColor}20` : theme.backgroundColor,
        color: theme.textColor,
      } as React.CSSProperties}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar"
          style={{
            '--scrollbar-bg': theme.backgroundColor,
            '--scrollbar-thumb': hexToRgba(theme.primaryColor, 0.25),
            '--scrollbar-thumb-hover': hexToRgba(theme.primaryColor, 0.40),
            '--scrollbar-track': theme.surfaceColor
          } as React.CSSProperties & Record<string, string>}
        >

          <div className={`min-h-full w-full flex flex-col ${(isOtp || isLimitExceeded) ? "relative items-center justify-center" : ''} ${viewMode === 'widget' ? '' : 'max-w-[95%] md:max-w-[90%] lg:max-w-[85%] xl:max-w-5xl 2xl:max-w-6xl mx-auto'
            }`}>
            {isLimitExceeded ? <div className='absolute rounded-xl inset-0 flex items-center justify-center bg-black/30'>
              <BookingForm onSubmit={handleBookingSubmit} mode={mode} isLoading={isLoading} theme={theme} name={userName}></BookingForm>
            </div>
              :
              isOtp ?
                <div className='absolute rounded-xl inset-0 flex items-center justify-center bg-black/30'>
                  <ConfirmationCard onSubmit={handleOtpSubmit} bookingDetails={bookingDetails} resendOTP={handleBookingSubmit} isLoading={isLoading} theme={theme}></ConfirmationCard>
                </div>
                :
                messages.length === 0 ? (
                  <InitialChatScreen
                    userName={userName}
                    theme={theme}
                    companyName={companyName}
                    onQuestionSelect={handleSendMessage}
                  />
                ) : (
                  <MessageList
                    messages={messages}
                    theme={theme}
                    viewMode={viewMode}
                    agentName={agentName}
                  />
                )
            }
            <div ref={messagesEndRef} />
          </div>

        </div>
        {!(isLimitExceeded || isOtp) &&
          <div className='px-0 pb-2 pt-1'>
            <div className="flex flex-col space-y-2 px-1 ">

              {/* Input Field Container */}
              <div
                className="flex items-center border transition-colors duration-300 px-1 py-1  mb-0 shadow-sm rounded-2xl"
                style={{
                  backgroundColor: theme.surfaceColor,
                  borderColor: theme.borderColor,
                }}
              >

                {/* Textarea */}
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  autoFocus
                  placeholder={
                    isListening
                      ? "Listening..."
                      : `Type something...`
                  }
                  disabled={isLoading}
                  className="flex-1 mx-3 py-2 px-1 resize-none focus:outline-none custom-scrollbar disabled:opacity-50 bg-transparent text-sm leading-relaxed rounded-xl"
                  style={{
                    color: theme.textColor,
                    height: "40px",
                    marginTop: "4px",

                  }}
                  rows={1}
                />

                {/* Right-side actions */}
                <div className="flex items-center space-x-2">
                  <MicrophoneInput
                    theme={theme}
                    onSpeechRecognized={handleSpeechRecognized}
                    isListening={isListening}
                    onListeningChange={setIsListening}
                    disabled={isLoading}
                  />


                  <button
                    onClick={() => {
                      if (inputValue.trim() || uploadedFiles.length > 0) {
                        handleSendMessage();
                      }
                      else {
                        handleVoiceButtonClick();
                      }
                    }}
                    disabled={isLoading}
                    className={`p-2 rounded-full transition-all duration-200 hover:opacity-90 disabled:opacity-50 flex items-center justify-center ${!(inputValue.trim() || uploadedFiles.length > 0) ? "animate-pulse shadow-md hover:scale-105" : ""
                      }`}
                    style={{
                      backgroundColor:
                        inputValue.trim() || uploadedFiles.length > 0
                          ? theme.primaryColor
                          : theme.primaryColor, // Always use primary color for voice button now
                      color: "white",
                      opacity: inputValue.trim() || uploadedFiles.length > 0 ? 1 : 0.9,
                    }}
                    title={inputValue.trim() || uploadedFiles.length > 0 ? "Send message" : "Start Voice Call"}
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : inputValue.trim() || uploadedFiles.length > 0 ? (
                      <ArrowUp className="w-5 h-5" />
                    ) : (
                      <AudioLines className="w-5 h-5" />
                    )}
                  </button>

                </div>
              </div>
            </div>
          </div>}

      </div>

      <input
        ref={fileInputRef}
        type="file"
        title='file'
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          handleFileSelect(files);
        }}
      />
    </div>
  );
};

export default ChatInterface;